import uuid from 'uuid-random';
import { ApolloClient, gql } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import {
  NotificationsQuery,
  NotificationsQuery_notifications as Notification
} from '../generated/types/NotificationsQuery';
import { throwQueryError } from '../lib/apollo/throwQueryError';
import { useLocalQuery } from './useLocalQuery';

export const typeDefs = gql`
  type Notification {
    id: String!
    type: String!
    message: String!
  }
`;

export const NOTIFICATIONS_QUERY = gql`
  query NotificationsQuery {
    notifications @client {
      id
      type
      message
    }
  }
`;

const updateCache = <T>(
  client: ApolloClient<T>,
  update: (notifications: Notification[]) => Notification[]
) =>
  client.writeQuery<NotificationsQuery>({
    query: NOTIFICATIONS_QUERY,
    data: {
      notifications: update(
        client.readQuery<NotificationsQuery>({ query: NOTIFICATIONS_QUERY })?.notifications || []
      )
    }
  });

export const useNotifications = () => {
  const { data, error, client } = useLocalQuery<NotificationsQuery>(NOTIFICATIONS_QUERY);
  const notifications = useMemo(() => data?.notifications || [], [data]);

  const addNotification = useCallback(
    (notification: Omit<Notification, '__typename' | 'id'>) =>
      updateCache(client, notifications => [
        ...notifications,
        { __typename: 'Notification', id: uuid(), ...notification }
      ]),
    [client]
  );

  const removeNotification = useCallback(
    (id: string) =>
      updateCache(client, notifications =>
        notifications.filter(notificaton => notificaton.id !== id)
      ),
    [client]
  );

  if (error) throwQueryError(error);

  return {
    notifications,
    addNotification,
    removeNotification
  };
};
